/**
 * ImageCarousel — Tinder-style swipeable image carousel for food cards.
 * Supports user photos, Google Places photos, and Google Maps static pin.
 * Optimized: lazy loading, WebP, responsive srcSet, blur placeholder.
 */
import { useState, useRef, useCallback, memo } from 'react';
import { ChevronLeft, ChevronRight, Navigation2, Camera, MapPin, ImageOff } from 'lucide-react';

/** Convert image URL to optimized version with Cloudinary transforms or query params */
const optimizeUrl = (url, width = 800) => {
  if (!url) return url;
  // Cloudinary: insert transforms
  if (url.includes('res.cloudinary.com') && url.includes('/upload/')) {
    return url.replace('/upload/', `/upload/w_${width},f_auto,q_auto/`);
  }
  // Google Places photos: add maxwidth
  if (url.includes('maps.googleapis.com/maps/api/place/photo')) {
    return url.includes('maxwidth') ? url : `${url}&maxwidth=${width}`;
  }
  // Unsplash: use w param
  if (url.includes('unsplash.com')) {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}w=${width}&q=80&fm=webp`;
  }
  return url;
};

const ImageCarousel = memo(({
  images = [],
  dishName = '',
  restaurant = '',
  location = '',
  rating,
  priceSymbol,
  distance,
  isOpen,
  cuisine,
  vibe,
  claimed,
  onAddPhoto,
  testId = '',
}) => {
  const [idx, setIdx] = useState(0);
  const [brokenUrls, setBrokenUrls] = useState(new Set());
  const touchRef = useRef({ startX: 0 });

  // Filter out broken images
  const validImages = images.filter(img => !brokenUrls.has(img.url));
  const total = validImages.length;
  const current = validImages[idx] || {};
  const isMap = current.type === 'location_pin';

  const prev = useCallback(() => setIdx(i => Math.max(0, i - 1)), []);
  const next = useCallback(() => setIdx(i => Math.min(total - 1, i + 1)), [total]);

  const handleTouchStart = useCallback((e) => {
    touchRef.current.startX = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (total <= 1) return;
    const dx = e.changedTouches[0].clientX - touchRef.current.startX;
    if (Math.abs(dx) > 40) {
      if (dx < 0) next();
      else prev();
    }
  }, [total, next, prev]);

  const handleImgError = useCallback((url) => {
    setBrokenUrls(prev => new Set([...prev, url]));
    // Auto-advance if current image broke
    if (idx > 0 && idx >= total - 1) setIdx(i => Math.max(0, i - 1));
  }, [idx, total]);

  // Fallback — no valid images at all
  if (!total) {
    const mapQuery = `${restaurant || dishName} ${location}`.trim();
    return (
      <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`}
        target="_blank" rel="noopener noreferrer"
        className="relative aspect-[4/3] md:aspect-[16/10] bg-gradient-to-br from-slate-100 via-slate-50 to-orange-50 flex flex-col items-center justify-center cursor-pointer hover:from-slate-200 hover:to-orange-100 transition-all rounded-t-[22px]"
        data-testid={`carousel-empty-${testId}`}>
        <div className="w-14 h-14 rounded-full bg-[#FF6B35]/10 flex items-center justify-center mb-2">
          <Navigation2 className="w-7 h-7 text-[#FF6B35]" />
        </div>
        <p className="text-sm font-bold text-gray-800">{restaurant || dishName}</p>
        {location && <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" />{location}</p>}
        <div className="mt-2.5 flex items-center gap-1.5 px-4 py-2 bg-[#FF6B35] text-white rounded-full text-xs font-semibold shadow-md">
          <Navigation2 className="w-3.5 h-3.5" /> Get Directions
        </div>
      </a>
    );
  }

  return (
    <div className="relative aspect-[4/3] md:aspect-[16/10] overflow-hidden group/carousel rounded-t-[22px]"
      onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}
      data-testid={`carousel-${testId}`}>

      {/* Image / Map slide */}
      {isMap ? (
        <a href={current.directions_url || '#'} target="_blank" rel="noopener noreferrer" className="block w-full h-full relative">
          <img src={current.url} alt="Location map" className="w-full h-full object-cover"
            onError={() => handleImgError(current.url)} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-5 py-2 bg-[#FF6B35] text-white rounded-full text-sm font-bold shadow-lg hover:bg-[#E55A2B] transition-colors z-10">
            <Navigation2 className="w-4 h-4" /> Get Directions
          </div>
        </a>
      ) : (
        <div className="w-full h-full relative">
          {/* Blur placeholder background */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-200 to-orange-100 animate-pulse" style={{ display: current.loaded === false ? 'block' : 'none' }} />
          <img
            src={optimizeUrl(current.url, 800)}
            srcSet={`${optimizeUrl(current.url, 400)} 400w, ${optimizeUrl(current.url, 800)} 800w`}
            sizes="(max-width: 640px) 400px, 800px"
            alt={dishName}
            className="w-full h-full object-cover group-hover/carousel:scale-[1.02] transition-transform duration-500"
            loading={idx === 0 ? "eager" : "lazy"}
            decoding="async"
            onError={() => handleImgError(current.url)}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent pointer-events-none" />
        </div>
      )}

      {/* ── Top bar: Rating/Price/Distance + Open + Add Photo ── */}
      {!isMap && (
        <div className="absolute top-0 inset-x-0 p-3 flex items-start justify-between z-10">
          {/* Left: Rating pill + Open badge */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <div className="flex items-center gap-1.5 bg-black/[0.32] backdrop-blur-md border border-white/[0.12] px-2.5 py-1 rounded-full">
              <svg className="w-3 h-3 fill-[#F4C430] text-[#F4C430]" viewBox="0 0 24 24"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
              <span className="text-white font-semibold text-xs">{rating || '4.5'}</span>
              <span className="text-white/30 text-xs">|</span>
              <span className="text-white text-xs">{priceSymbol || '$$'}</span>
              {distance && (
                <>
                  <span className="text-white/30 text-xs">|</span>
                  <span className="text-white text-xs">{distance}</span>
                </>
              )}
            </div>
            {isOpen && (
              <span className="bg-emerald-500/[0.22] backdrop-blur-sm border border-emerald-400/30 px-2 py-0.5 rounded-full">
                <span className="text-white text-[10px] font-semibold">Open</span>
              </span>
            )}
          </div>

          {/* Right: Slide counter + Add Photo */}
          <div className="flex items-center gap-1.5">
            {total > 1 && (
              <span className="bg-black/[0.32] backdrop-blur-md border border-white/[0.12] text-white text-[10px] font-medium px-2 py-0.5 rounded-full">
                {idx + 1}/{total}
              </span>
            )}
            {onAddPhoto && (
              <button onClick={onAddPhoto}
                className="flex items-center gap-1 bg-white/90 backdrop-blur-md px-2.5 py-1 rounded-full text-[10px] font-bold text-gray-800 shadow-sm hover:bg-white transition-all"
                data-testid={`add-photo-${testId}`}>
                <Camera className="w-3 h-3 text-[#FF6B35]" /> Add Photo
              </button>
            )}
          </div>
        </div>
      )}

      {/* Claimed badge */}
      {claimed && !isMap && (
        <div className="absolute top-12 left-3 bg-emerald-500/90 backdrop-blur-md px-2 py-0.5 rounded-full z-10">
          <span className="text-white text-[10px] font-bold">Collected</span>
        </div>
      )}

      {/* ── Bottom bar: Cuisine/Vibe tags + Attribution ── */}
      {!isMap && (
        <div className="absolute bottom-0 inset-x-0 p-3 pb-7 flex items-end justify-between z-10">
          {/* Left: Cuisine and vibe tags */}
          <div className="flex flex-wrap gap-1">
            {cuisine && <span className="bg-white/[0.14] backdrop-blur-md border border-white/[0.22] text-white text-[10px] font-medium px-2 py-0.5 rounded-[10px]">{cuisine}</span>}
            {vibe && <span className="bg-white/[0.14] backdrop-blur-md border border-white/[0.22] text-white text-[10px] font-medium px-2 py-0.5 rounded-[10px]">{vibe}</span>}
          </div>
          {/* Right: Attribution */}
          {current.attribution && (
            <span className="bg-black/[0.32] backdrop-blur-md border border-white/[0.12] text-white/80 text-[9px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
              data-testid={`attribution-${testId}`}>
              {current.attribution}
            </span>
          )}
        </div>
      )}

      {/* Navigation arrows (desktop hover) */}
      {total > 1 && (
        <>
          {idx > 0 && (
            <button onClick={prev}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover/carousel:opacity-100 transition-opacity z-20"
              data-testid={`carousel-prev-${testId}`}>
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          {idx < total - 1 && (
            <button onClick={next}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover/carousel:opacity-100 transition-opacity z-20"
              data-testid={`carousel-next-${testId}`}>
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </>
      )}

      {/* Dot indicators — positioned clearly below content, above bottom edge */}
      {total > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-20 bg-black/25 backdrop-blur-sm px-2 py-1 rounded-full">
          {validImages.map((img, i) => (
            <button key={i} onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${i === idx
                ? (img.type === 'location_pin' ? 'bg-[#FF6B35] w-4' : 'bg-white w-4')
                : (img.type === 'location_pin' ? 'bg-[#FF6B35]/40 w-1.5' : 'bg-white/40 w-1.5')
              }`}
              data-testid={`carousel-dot-${i}-${testId}`} />
          ))}
        </div>
      )}
    </div>
  );
});

ImageCarousel.displayName = 'ImageCarousel';

export default ImageCarousel;
